import React from 'react'
import { createPortal } from 'react-dom'

import { resolveAlarmSafeProductName } from '../../../../common/helpers/alarmSafeNotes'

import {
  PRODUCT_TAB_LABELS,
  ProductTab,
  getProductMedia,
  productTabs,
} from '../../../img/productMedia/productMediaMap'
import { ProductModelStage } from './ProductModelDialog'
import ProductPhotosTab from './viewer/ProductPhotosTab'
import ProductSceneTab from './viewer/ProductSceneTab'
import ProductVideoTab from './viewer/ProductVideoTab'
import ViewerCloseButton from './viewer/ViewerCloseButton'

//@ts-ignore
import styles from './ProductViewer.module.css'

/** Previous / next product in the Job Summary (Monday 13246279222). */
export interface ProductViewerNav {
  /** Position of the open product, 0-based. */
  index: number
  onNext: () => void
  onPrev: () => void
  total: number
}

export interface ProductViewerProps {
  /** Arrows, swipe and ←/→ between products; omitted on ProductLineup. */
  nav?: ProductViewerNav
  onClose: () => void
  /** Identity of the open product when the same `type` can appear twice
   * (Job Summary: `field:type`); defaults to `type`. */
  productKey?: string
  /** Main photo URL, `productPhotoMap[type]`. */
  photo: string
  /** Firebase product `type`, the key of the media maps. */
  type: string
}

const Chevron = ({ flip }: { flip?: boolean }) => (
  <svg aria-hidden="true" height="18" viewBox="0 0 24 24" width="18">
    <path
      d={flip ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

// The Job Summary's product viewer: one product at a time, with a tab for each
// kind of media that product has (Products / Scene / Video / 3D Model). Tabs
// without content are not rendered, and a single tab hides the strip.
//
// With `nav`, arrows / swipe / ←→ step to the previous or next product. The
// viewer is not remounted on a product change (no `key`): the body pin, the
// focus and the restored scroll position all survive the switch.
export default function ProductViewer({
  nav,
  onClose,
  photo,
  productKey,
  type,
}: ProductViewerProps) {
  const media = React.useMemo(() => getProductMedia(type, photo), [type, photo])
  const tabs = React.useMemo(() => productTabs(media), [media])
  // The customer sees this title (also in ?mode=sign): same alarm-safe name
  // as every other product label; `type` stays the media-map key.
  const name = resolveAlarmSafeProductName(type)
  const [active, setActive] = React.useState<ProductTab | undefined>(tabs[0])
  // A product change always lands on the first tab (Products), never on the
  // 3D Model of the product before (reset during render, no flash).
  const shownKey = productKey ?? type
  const [activeKey, setActiveKey] = React.useState(shownKey)
  if (activeKey !== shownKey) {
    setActiveKey(shownKey)
    setActive(tabs[0])
  }
  const current = active && tabs.includes(active) ? active : tabs[0]

  const hasPrev = !!nav && nav.index > 0
  const hasNext = !!nav && nav.index < nav.total - 1
  const navRef = React.useRef(nav)
  navRef.current = nav
  const prevRef = React.useRef<HTMLButtonElement>(null)
  const nextRef = React.useRef<HTMLButtonElement>(null)
  // Set when a product change starts with the focus on an arrow, which may
  // be gone after the change (end of the list).
  const refocusRef = React.useRef(false)
  const go = React.useCallback((move: () => void): void => {
    const focused = document.activeElement
    refocusRef.current =
      !!focused && (focused === prevRef.current || focused === nextRef.current)
    move()
  }, [])
  const goRef = React.useRef(go)
  goRef.current = go

  const closeRef = React.useRef<HTMLButtonElement>(null)
  const tabRefs = React.useRef<Partial<Record<ProductTab, HTMLButtonElement>>>(
    {},
  )

  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose

  React.useEffect(() => {
    closeRef.current?.focus()
  }, [])

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCloseRef.current()
        return
      }
      const step =
        event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
      const current = navRef.current
      if (!step || !current || event.defaultPrevented) return
      // ←/→ keep switching tabs on the tab strip and turning the 3D model.
      const { target } = event
      if (
        target instanceof Element &&
        target.closest('[role="tablist"], model-viewer, input, textarea')
      ) {
        return
      }
      if (step < 0 && current.index > 0) {
        event.preventDefault()
        goRef.current(current.onPrev)
      } else if (step > 0 && current.index < current.total - 1) {
        event.preventDefault()
        goRef.current(current.onNext)
      }
    }
    document.addEventListener('keydown', onKey)
    // iOS ignores overflow: hidden on body, so the report (wider than a
    // phone) kept scrolling and panning behind the sheet. Pinning the body
    // with position: fixed locks it on every engine; the scroll position is
    // put back on close.
    const { body } = document
    const { scrollX, scrollY } = window
    const previous = {
      left: body.style.left,
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    }
    body.style.left = `${-scrollX}px`
    body.style.overflow = 'hidden'
    body.style.position = 'fixed'
    body.style.top = `${-scrollY}px`
    body.style.width = '100%'
    return () => {
      document.removeEventListener('keydown', onKey)
      Object.assign(body.style, previous)
      // global.css sets scroll-behavior: smooth on every element, which would
      // animate the page from the top back to here; jump instantly instead.
      const html = document.documentElement
      const behavior = html.style.scrollBehavior
      html.style.scrollBehavior = 'auto'
      window.scrollTo(scrollX, scrollY)
      html.style.scrollBehavior = behavior
    }
  }, [])

  // An arrow that just disappeared at an end of the list took the focus with
  // it: hand it to the other arrow, or to the X.
  React.useLayoutEffect(() => {
    if (!refocusRef.current) return
    refocusRef.current = false
    const focused = document.activeElement
    if (focused && focused !== document.body) return
    ;(prevRef.current ?? nextRef.current ?? closeRef.current)?.focus()
  }, [nav?.index])

  const onTabKey = (event: React.KeyboardEvent): void => {
    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
    if (!step || !current) return
    event.preventDefault()
    const next =
      tabs[(tabs.indexOf(current) + step + tabs.length) % tabs.length]
    if (!next) return
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  return createPortal(
    <div className={styles['scrim']} onClick={onClose} role="presentation">
      <div
        aria-label={name}
        aria-modal="true"
        className={styles['sheet']}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className={styles['header']}>
          <div className={styles['heading']}>
            <h2 className={styles['title']}>{name}</h2>
            {nav && nav.total > 1 && (
              <span aria-live="polite" className={styles['position']}>
                {nav.index + 1} / {nav.total}
              </span>
            )}
          </div>
          <ViewerCloseButton onClick={onClose} ref={closeRef} />
        </div>
        {tabs.length > 1 && (
          <div
            aria-label="Product media"
            className={styles['tabs']}
            onKeyDown={onTabKey}
            role="tablist"
          >
            {tabs.map((tab) => (
              <button
                aria-selected={tab === current}
                className={styles['tab']}
                id={`product-viewer-tab-${tab}`}
                key={tab}
                onClick={() => setActive(tab)}
                ref={(node) => {
                  if (node) tabRefs.current[tab] = node
                }}
                role="tab"
                tabIndex={tab === current ? 0 : -1}
                type="button"
              >
                {PRODUCT_TAB_LABELS[tab]}
              </button>
            ))}
          </div>
        )}
        <div
          aria-labelledby={
            tabs.length > 1 ? `product-viewer-tab-${current}` : undefined
          }
          className={styles['panel']}
          role={tabs.length > 1 ? 'tabpanel' : undefined}
        >
          {current === 'products' && (
            <ProductPhotosTab
              alt={name}
              key={shownKey}
              onSwipeNext={hasNext ? nav?.onNext : undefined}
              onSwipePrev={hasPrev ? nav?.onPrev : undefined}
              photos={media.photos}
            />
          )}
          {current === 'scene' && (
            <ProductSceneTab alt={name} scenes={media.scenes} />
          )}
          {current === 'video' && media.video && (
            <ProductVideoTab
              poster={media.video.poster}
              src={media.video.src}
            />
          )}
          {current === 'model' && media.modelUrl && (
            <ProductModelStage
              alt={name}
              key={shownKey}
              poster={photo}
              src={media.modelUrl}
              stageClassName={styles['model-stage']}
            />
          )}
          {hasPrev && (
            <button
              aria-label="Previous product"
              className={`${styles['nav']} ${styles['nav-prev']}`}
              onClick={() => nav && go(nav.onPrev)}
              ref={prevRef}
              type="button"
            >
              <Chevron />
            </button>
          )}
          {hasNext && (
            <button
              aria-label="Next product"
              className={`${styles['nav']} ${styles['nav-next']}`}
              onClick={() => nav && go(nav.onNext)}
              ref={nextRef}
              type="button"
            >
              <Chevron flip />
            </button>
          )}
        </div>
        {current === 'model' && <p className={styles['hint']}>Drag to turn</p>}
      </div>
    </div>,
    document.body,
  )
}
